import { useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, Trash2, Loader2, Lock } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Row = ({ testId, icon: Icon, label, hint, onClick, busy, danger }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={busy}
    className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors disabled:opacity-60 ${
      danger ? "hover:bg-talanta-red/5 text-talanta-red" : "hover:bg-talanta-cream text-talanta-charcoal"
    }`}
  >
    {busy ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <Icon className="h-4 w-4 shrink-0" />}
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-semibold">{label}</span>
      <span className="block text-xs opacity-60">{hint}</span>
    </span>
  </button>
);

export const PrivacyDataCard = ({ onClose }) => {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const download = async () => {
    setBusy("export");
    try {
      const { data } = await api.get("/auth/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `one-talanta-id-${user.talanta_id}.json` });
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Your data export has been downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export your data");
    } finally {
      setBusy("");
    }
  };

  const logoutAll = async () => {
    setBusy("logout-all");
    try {
      const { data } = await api.post("/auth/logout-all");
      tokenStore.clear();
      setUser(null);
      toast.success(`Signed out of ${data.revoked} device${data.revoked === 1 ? "" : "s"}`);
      onClose();
    } catch (e) {
      toast.error("Could not sign out everywhere");
    } finally {
      setBusy("");
    }
  };

  const deleteAccount = async () => {
    setBusy("delete");
    try {
      await api.delete("/auth/me");
      tokenStore.clear();
      setUser(null);
      toast.success("Your One Talanta ID and data have been deleted");
      onClose();
    } catch (e) {
      toast.error("Could not delete your account");
    } finally {
      setBusy("");
      setConfirmDelete(false);
    }
  };

  return (
    <div className="rounded-xl border border-talanta-green/10 bg-white p-2" data-testid="privacy-data-card">
      <div className="flex items-center gap-2 px-3 pt-2 pb-1 text-xs uppercase tracking-wider text-talanta-charcoal/50">
        <Lock className="h-3.5 w-3.5" /> Privacy &amp; your data
      </div>
      <Row testId="privacy-export-btn" icon={Download} label="Download my data" hint="JSON copy of everything we hold (DPA 2019 §26)" onClick={download} busy={busy === "export"} />
      <Row testId="privacy-logout-all-btn" icon={LogOut} label="Sign out everywhere" hint="Revoke every active session on all devices" onClick={logoutAll} busy={busy === "logout-all"} />
      <Row testId="privacy-delete-btn" icon={Trash2} label="Delete my account" hint="Permanently erase your One Talanta ID" onClick={() => setConfirmDelete(true)} busy={busy === "delete"} danger />
      {user.consent?.accepted_at && (
        <p className="px-3 pb-2 pt-1 text-[11px] text-talanta-charcoal/40" data-testid="privacy-consent-info">
          Privacy Policy v{user.consent.policy_version} accepted {new Date(user.consent.accepted_at).toLocaleDateString("en-KE")}.
        </p>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete your One Talanta ID?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently erases your profile, sessions, linked-athlete reference and pillar subscriptions for {user.phone}. It cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel">Keep my account</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-account-confirm" onClick={deleteAccount} className="bg-talanta-red hover:bg-talanta-red/90">
              Yes, delete everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
